// DashboardActivityList.tsx - Original component without changes
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Spinner } from 'react-bootstrap';
import { message } from 'antd';
import { Calendar, Filter, RefreshCw, Maximize2 } from 'lucide-react';
import DashboardActivityItem from './DashboardActivityItem';
import { getAllActivity } from '../../services/activityService';
import activityTypes from '../ActivityFeed/activityTypes';
import { Activity } from '../ActivityFeed/types';
import PopupComponent from '../../components/PopupComponent';
import DashboardActivityListExpanded from './DashboardActivityListExpanded'; // NEW: Import expanded wrapper
import './DashboardActivityItem.css';
import logger from '../../utils/logger';


// Console logs for debugging
logger.log('📱 DashboardActivityList component loaded');

interface DashboardActivityListProps {
  fromDate: string;
  toDate: string;
  organization: boolean;
  userId: string;
  selectedTeamId: string;
  title?: string;
}

interface ActivityFilterState {
  types: string[];
  teams: string[];
  userIds: string[];
  dateRange: string;
}

interface TempFilterState extends ActivityFilterState {
  // Temporary state for popup before applying
}

const DashboardActivityList: React.FC<DashboardActivityListProps> = ({
  fromDate,
  toDate,
  organization,
  userId,
  selectedTeamId,
  title = "Recent Activities"
}) => {
  logger.log('🔄 DashboardActivityList rendered with params:', {
    fromDate, toDate, organization, userId, selectedTeamId
  });

  // State management - reusing ActivityFeed logic
  const [displayedActivities, setDisplayedActivities] = useState<Activity[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);
  const [showExpandedView, setShowExpandedView] = useState(false); // NEW: For expanded view
  
  // Filter state - following same pattern as ActivityFeed
  const [filters, setFilters] = useState<ActivityFilterState>({
    types: [],
    teams: selectedTeamId ? [selectedTeamId] : [],
    userIds: userId ? [userId] : [],
    dateRange: 'Last 7 Days'
  });

  // Temporary filter state for popup (before applying)
  const [tempFilters, setTempFilters] = useState<TempFilterState>({
    types: [],
    teams: selectedTeamId ? [selectedTeamId] : [],
    userIds: userId ? [userId] : [],
    dateRange: 'Last 7 Days'
  });

  const itemsPerPage = 15;
  const containerRef = useRef<HTMLDivElement>(null);
  const initialLoadRef = useRef(false);

  // Format date for display - reusing ActivityFeed logic
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Load activities - adapted from ActivityFeed logic
  const loadActivities = useCallback(async (page: number, reset = false) => {
    logger.log('🔍 Loading activities, page:', page, 'reset:', reset);
    
    if (!reset && (isLoadingMore || !hasMore)) return;

    if (reset) {
      setIsLoading(true);
      logger.log('🔄 Initial loading started');
    } else {
      setIsLoadingMore(true);
      logger.log('📄 Loading more activities');
    }

    try {
      // Date parameters logic - reusing from ActivityFeed
      let dateParams = {};
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Handle different date range options
      if (filters.dateRange.includes(',')) {
        // Custom date range
        const [start, end] = filters.dateRange.split(',');
        dateParams = {
          startedAt: start,
          endedAt: end
        };
      } else {
        // Predefined ranges
        switch (filters.dateRange) {
          case 'Today':
            dateParams = {
              startedAt: today.toISOString(),
              endedAt: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()
            };
            break;
          case 'Yesterday':
            const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
            dateParams = {
              startedAt: yesterday.toISOString(),
              endedAt: today.toISOString()
            };
            break;
          case 'Last 7 Days':
            dateParams = {
              startedAt: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
              endedAt: new Date().toISOString()
            };
            break;
          case 'Last 30 Days':
            dateParams = {
              startedAt: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
              endedAt: new Date().toISOString()
            };
            break;
          default:
            // Use dashboard date params as fallback
            dateParams = {
              startedAt: fromDate,
              endedAt: toDate
            };
        }
      }

      // Build API parameters
      const params = {
        page,
        perPage: itemsPerPage,
        type: filters.types.length > 0 ? filters.types.join(',') : '', // Handle multiple types
        teams: filters.teams.length > 0 ? filters.teams.join(',') : (selectedTeamId || ''),
        userIds: filters.userIds.length > 0 ? filters.userIds.join(',') : (userId || ''),
        orderBy: '_id',
        isAscending: false,
        byOrganization: organization,
        ...dateParams
      };

      // Filter out empty parameters
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== '')
      );

      logger.log('📡 API call with parameters:', filteredParams);
      
      const response = await getAllActivity(filteredParams);
      const activities = response.data.data;
      
      logger.log('✅ API response:', {
        activitiesCount: activities.length,
        total: response.data.total,
        page: page
      });

      setTotal(response.data.total);

      if (activities.length === 0) {
        setHasMore(false);
        logger.log('📄 No more activities to load');
      } else {
        setDisplayedActivities(prev => reset ? activities : [...prev, ...activities]);
        setCurrentPage(page + 1);
        logger.log('📋 Activities updated, total displayed:', reset ? activities.length : displayedActivities.length + activities.length);
      }
    } catch (error) {
      console.error('❌ Error fetching activities:', error);
      message.error('Failed to load activities');
      setHasMore(false);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [filters, fromDate, toDate, organization, userId, selectedTeamId, isLoadingMore, hasMore, displayedActivities.length]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || isLoading || isLoadingMore || !hasMore) return;

    const bottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 10;
    if (bottom) {
      logger.log('📄 Reached bottom, loading more');
      loadActivities(currentPage, false);
    }
  }, [currentPage, isLoading, isLoadingMore, hasMore, loadActivities]);

  // Handle edit activity - reusing from ActivityTab
  const handleEditActivity = useCallback(async (name: string, id: string, action: string) => {
    logger.log('✏️ Edit activity:', { name, id, action });
    // You'll need to implement this based on your existing edit modal logic
    // For now, just log the action
    message.info(`Edit functionality for activity ${id}`);
  }, []);

  // Filter handlers for popup
  const handleActivityTypeToggle = (type: string) => {
    logger.log('🔍 Activity type toggled:', type);
    setTempFilters(prev => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter(t => t !== type) // Remove if exists
        : [...prev.types, type] // Add if doesn't exist
    }));
  };

  const handleSelectAllActivityTypes = () => {
    const allTypes = Object.keys(activityTypes);
    logger.log('🔍 Select all activity types:', allTypes);
    setTempFilters(prev => ({
      ...prev,
      types: prev.types.length === allTypes.length ? [] : allTypes
    }));
  };

  const handleDateRangeChange = (range: string) => {
    logger.log('📅 Date range changed in popup:', range);
    setTempFilters(prev => ({ ...prev, dateRange: range }));
  };

  const openFiltersPopup = () => {
    logger.log('🔍 Opening filters popup');
    // Initialize temp filters with current filters
    setTempFilters({ ...filters });
    setShowFiltersPopup(true);
  };

  const closeFiltersPopup = () => {
    logger.log('🔍 Closing filters popup');
    setShowFiltersPopup(false);
  };

  const applyFilters = () => {
    logger.log('🔍 Applying filters:', tempFilters);
    setFilters({ ...tempFilters });
    setShowFiltersPopup(false);
    message.success('Filters applied successfully');
  };

  const clearAllFilters = () => {
    logger.log('🔍 Clearing all filters');
    const clearedFilters = {
      types: [],
      teams: [],
      userIds: [],
      dateRange: 'Last 7 Days'
    };
    setTempFilters(clearedFilters);
    setFilters(clearedFilters);
    setShowFiltersPopup(false);
    message.success('All filters cleared');
  };

  const handleRefresh = () => {
    logger.log('🔄 Manual refresh triggered');
    setCurrentPage(1);
    setDisplayedActivities([]);
    setHasMore(true);
    loadActivities(1, true);
  };

  // Load activities when filters change (but not on initial load)
  useEffect(() => {
    if (initialLoadRef.current) {
      logger.log('🔄 Filters changed, reloading activities');
      setCurrentPage(1);
      setDisplayedActivities([]);
      setHasMore(true);
      loadActivities(1, true);
    }
  }, [filters.types, filters.teams, filters.userIds, filters.dateRange]);

  // Initial load only
  useEffect(() => {
    if (!initialLoadRef.current) {
      logger.log('🔓 Component mounted, loading initial activities');
      setCurrentPage(1);
      setDisplayedActivities([]);
      setHasMore(true);
      loadActivities(1, true);
      initialLoadRef.current = true;
    }
  }, []);

  // Update temp filters when popup opens
  useEffect(() => {
    if (showFiltersPopup) {
      logger.log('🔄 Popup opened, syncing temp filters with current filters');
      setTempFilters({ ...filters });
    }
  }, [showFiltersPopup, filters]);

  // Activity type options for filter
  const activityTypeOptions = Object.entries(activityTypes).map(([key, config]) => ({
    value: key,
    label: config.label,
    icon: config.icon
  }));

  // Debug logging for activity types
  logger.log('🔍 Available activity types:', activityTypes);
  logger.log('🔍 Activity type options:', activityTypeOptions);

  // Date range options
  const dateRangeOptions = [
    { value: 'Today', label: 'Today' },
    { value: 'Yesterday', label: 'Yesterday' },
    { value: 'Last 7 Days', label: 'Last 7 Days' },
    { value: 'Last 30 Days', label: 'Last 30 Days' },
    { value: 'This Month', label: 'This Month' },
    { value: 'Last Month', label: 'Last Month' },
    { value: 'This Year', label: 'This Year' }
  ];

  // Get active filters count for display
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.types.length > 0) count++;
    if (filters.teams.length > 0) count++;
    if (filters.userIds.length > 0) count++;
    if (filters.dateRange !== 'Last 7 Days') count++; // Only count non-default
    return count;
  };

  // Get active filters summary for display
  const getActiveFiltersSummary = () => {
    const parts = [];
    if (filters.types.length > 0) {
      parts.push(`${filters.types.length} type${filters.types.length !== 1 ? 's' : ''}`);
    }
    if (filters.dateRange !== 'Last 7 Days') {
      parts.push(`date: ${filters.dateRange}`);
    }
    return parts.length > 0 ? parts.join(', ') : 'No filters active';
  };

  return (
    <Card className="dashboard-activity-list" style={{ 
      maxHeight: '500px',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      background: '#ffffff'
    }}>
      <CardHeader style={{
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        padding: '16px 20px',
        borderRadius: '12px 12px 0 0'
      }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <h5 className="mb-0 fw-bold text-dark">{title}</h5>
            <span style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              minWidth: '24px',
              textAlign: 'center',
              marginLeft: '8px'
            }}>
              {total.toLocaleString()}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {/* NEW: Expand button */}
            <button
              onClick={() => setShowExpandedView(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                background: 'white',
                color: '#3b82f6',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <Maximize2 size={16} />
              Expand
            </button>
            <button
              onClick={openFiltersPopup}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: getActiveFiltersCount() > 0 ? '#3b82f6' : 'white',
                color: getActiveFiltersCount() > 0 ? 'white' : '#374151',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
            >
              <Filter size={16} />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: '#ef4444',
                  color: 'white',
                  borderRadius: '10px',
                  width: '18px',
                  height: '18px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600'
                }}>
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: isLoading ? 0.5 : 1
              }}
            >
              <RefreshCw size={16} style={{
                animation: isLoading ? 'spin 1s linear infinite' : 'none'
              }} />
            </button>
          </div>
        </div>

        {/* Active Filters Summary */}
        {getActiveFiltersCount() > 0 && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#0369a1'
          }}>
            <strong>Active filters:</strong> {getActiveFiltersSummary()}
            <button
              onClick={clearAllFilters}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                color: '#dc2626',
                fontSize: '12px',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Clear all
            </button>
          </div>
        )}
      </CardHeader>

      <CardBody style={{
        padding: '0',
        height: 'calc(500px - 80px)',
        overflow: 'hidden'
      }}>
        {/* Loading State */}
        {isLoading && displayedActivities.length === 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            <Spinner animation="border" variant="primary" size="sm" />
            <span className="ms-2">Loading activities...</span>
          </div>
        )}

        {/* Activities List */}
        {displayedActivities.length > 0 && (
          <div 
            onScroll={handleScroll}
            ref={containerRef}
            style={{
              height: '100%',
              overflowY: 'auto',
              padding: '16px 20px'
            }}
          >
            {displayedActivities.map((activity, index) => (
              <div 
                key={activity._id}
                data-activity-type={activity.type}
              >
                <DashboardActivityItem
                  activity={activity}
                  handleEditModal={handleEditActivity}
                  name="activity"
                />
              </div>
            ))}

            {/* Load More Indicator */}
            {isLoadingMore && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px',
                background: '#f9fafb',
                borderTop: '1px solid #e5e7eb',
                color: '#6b7280',
                fontSize: '14px'
              }}>
                <Spinner animation="border" variant="primary" size="sm" />
                <span className="ms-2">Loading more...</span>
              </div>
            )}

            {/* End of List */}
            {!hasMore && displayedActivities.length > 0 && (
              <div style={{
                textAlign: 'center',
                padding: '16px',
                color: '#9ca3af',
                fontSize: '13px',
                borderTop: '1px solid #f1f5f9',
                background: '#fafbfc'
              }}>
                <span>You've reached the end of activities</span>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && displayedActivities.length === 0 && (
          <div style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div className="text-center py-5" style={{ maxWidth: '300px' }}>
              <Calendar size={48} className="text-muted mb-3" />
              <h5 className="text-muted mb-2">No Activities Found</h5>
              <p className="text-muted">
                {filters.types.length > 0 
                  ? 'Try adjusting your filters to see more results'
                  : 'No activities available for the selected time period'
                }
              </p>
              {filters.types.length > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="btn btn-outline-primary btn-sm"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        )}
      </CardBody>

      {/* Filters Popup */}
      <PopupComponent
        isOpen={showFiltersPopup}
        onClose={closeFiltersPopup}
        title="Activity Filters"
        size="md"
        footer={
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            padding: "0 8px",
            width: "100%"
          }}>
            <button 
              onClick={clearAllFilters}
              style={{
                padding: "8px 16px",
                backgroundColor: "#dc2626",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: "pointer"
              }}
            >
              Clear All
            </button>
            <div style={{ display: "flex", gap: "12px" }}>
              <button 
                onClick={closeFiltersPopup}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "white",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={applyFilters}
                style={{
                  padding: "8px 20px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor: "pointer"
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        }
      >
        <div style={{ padding: "20px" }}>
          {/* Activity Types Section */}
          <div style={{ marginBottom: "24px" }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px"
            }}>
              <label style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151"
              }}>
                Activity Types ({tempFilters.types.length} selected)
              </label>
              <button
                onClick={handleSelectAllActivityTypes}
                style={{
                  background: "none",
                  border: "1px solid #3b82f6",
                  color: "#3b82f6",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  cursor: "pointer"
                }}
              >
                {tempFilters.types.length === activityTypeOptions.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            <div style={{
              maxHeight: "200px",
              overflowY: "auto",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              padding: "8px"
            }}>
              {activityTypeOptions.map(option => (
                <label
                  key={option.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    margin: "2px 0",
                    cursor: "pointer",
                    borderRadius: "4px",
                    transition: "background-color 0.2s ease",
                    backgroundColor: tempFilters.types.includes(option.value) ? "#eff6ff" : "transparent"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = tempFilters.types.includes(option.value) ? "#eff6ff" : "transparent"}
                >
                  <input
                    type="checkbox"
                    checked={tempFilters.types.includes(option.value)}
                    onChange={() => handleActivityTypeToggle(option.value)}
                    style={{
                      marginRight: "8px",
                      width: "16px",
                      height: "16px",
                      cursor: "pointer"
                    }}
                  />
                  <span style={{
                    fontSize: "14px",
                    color: "#374151"
                  }}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Date Range Section */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px"
            }}>
              Date Range
            </label>
            <select
              value={tempFilters.dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                background: "white"
              }}
            >
              {dateRangeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Current Selection Summary */}
          <div style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            padding: "12px",
            fontSize: "13px",
            color: "#6b7280"
          }}>
            <strong>Current Selection:</strong>
            <div style={{ marginTop: "4px" }}>
              • {tempFilters.types.length} activity type{tempFilters.types.length !== 1 ? 's' : ''} selected
              <div>• Date range: {tempFilters.dateRange}</div>
            </div>
          </div>
        </div>
      </PopupComponent>

      {/* NEW: Expanded View Popup - Simple wrapper */}
      <DashboardActivityListExpanded
        isOpen={showExpandedView}
        onClose={() => setShowExpandedView(false)}
        fromDate={fromDate}
        toDate={toDate}
        organization={organization}
        userId={userId}
        selectedTeamId={selectedTeamId}
        title={title}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Card>
  );
};

export default DashboardActivityList;